import React, { useState, useEffect, useRef } from "react";
import Heading from "@/components/Heading";
import { Link, useParams, useLocation } from "react-router-dom";
import { Form } from "@unform/web";
import * as Yup from "yup";
import Input from "@/components/Input";
import InputMask from "@/components/InputMask";
import ModalPopup from "@/components/ModalPopup";
import {
	equalTo,
	validateCnpj,
	validateCpf,
	validatePassword,
} from "@/utils/validations";

import "bootstrap/dist/css/bootstrap-grid.min.css";
import { LabelContainer, Container, Success } from "./styles";
import { api } from "@/services/api";

function useQuery() {
	return new URLSearchParams(useLocation().search);
}

export const ResetPassword = () => {
	const query = useQuery();
	const { type } = useParams();
	const formRef = useRef(null);
	const [toogle, setToogle] = useState(true);
	const [recoveryToken, setRecoveryToken] = useState(null);
	const [submit, setSubmit] = useState(false);
	const [modalPopup, setModalPopup] = useState(false);
	const [modalContent, setModalContent] = useState({
		title: "",
		content: "",
	});

	useEffect(() => {
		setRecoveryToken(query.get("recovery_token"));
	}, [query]);

	async function handleSubmit(data) {
		Yup.addMethod(Yup.string, "equalTo", equalTo);
		Yup.addMethod(Yup.string, "validateCnpj", validateCnpj);
		Yup.addMethod(Yup.string, "validateCpf", validateCpf);
		Yup.addMethod(Yup.string, "validatePassword", validatePassword);

		try {
			let schema;
			if (type === "merchant") {
				schema = Yup.object().shape({
					cnpj: Yup.string()
						.validateCnpj("CNPJ inválido")
						.required("Campo obrigatório"),
					password: Yup.string()
						.validatePassword(
							"A senha deve conter pelo menos 8 caracteres, sendo uma letra maiúscula, uma letra minúscula e um número",
						)
						.required("Campo obrigatório"),
					passwordConfirm: Yup.string()
						.equalTo(Yup.ref("password"), "Senhas não correspondem")
						.required("Campo obrigatório"),
				});
			}

			if (type === "consumer") {
				schema = Yup.object().shape({
					cpf: Yup.string()
						.validateCpf("CPF inválido")
						.required("Campo obrigatório"),
					password: Yup.string()
						.validatePassword(
							"A senha deve conter pelo menos 8 caracteres, sendo uma letra maiúscula, uma letra minúscula e um número",
						)
						.required("Campo obrigatório"),
					passwordConfirm: Yup.string()
						.equalTo(Yup.ref("password"), "Senhas não correspondem")
						.required("Campo obrigatório"),
				});
			}

			await schema.validate(data, {
				abortEarly: false,
			});

			formRef.current.setErrors({});
			handleResetPassword(data);
		} catch (err) {
			if (err instanceof Yup.ValidationError) {
				const errorMessages = {};

				err.inner.forEach((error) => {
					errorMessages[error.path] = error.message;
				});

				formRef.current.setErrors(errorMessages);
			}
		}
	}

	function handleResetPassword(data) {
		setSubmit(true);
		data.key = recoveryToken;

		api.post(`${type}/redefine`, data)
			.then((res) => {
				setSubmit(false);

				if (res.data.status === 200) {
					setToogle(!toogle);
				}

				if (res.data.status === 500) {
					setModalPopup(true);
					setModalContent({
						title: "Ocorreu um problema!",
						content:
							res.data.validation_errors["consumer.password"][0],
					});
				}

				if (res.data.status === 400) {
					setModalPopup(true);
					setModalContent({
						title: "Ocorreu um problema!",
						content: res.data.message,
					});
				}
			})
			.catch((error) => {
				setSubmit(false);
				setModalPopup(true);
				setModalContent({
					title: "Ocorreu um problema!",
					content: "Ops! Não foi possível atender sua solicitação.",
				});
				console.error({ endPoint: `/${type}/redefine`, error });
			});
	}

	return (
		<Container>
			<ModalPopup
				visible={modalPopup}
				message={modalContent}
				onClick={() => setModalPopup(!modalPopup)}
			/>
			{toogle ? (
				<>
					<Heading>
						<p className='title'>
							<span>Redefinir</span> senha
						</p>
					</Heading>
					<section className='container-fluid mb-lg-5'>
						<div className='row justify-content-lg-center'>
							<div className='col-12 col-lg-4'>
								<span className='separator'></span>
								<Form
									ref={formRef}
									name='resetPassword'
									onSubmit={handleSubmit}
									className='content'>
									<p>
										Preencha os campos abaixo para redefinir
										sua nova senha.
									</p>

									{type === "merchant" && (
										<LabelContainer>
											<InputMask
												label='CNPJ'
												name='cnpj'
												mask='99.999.999/9999-99'
												placeholder='00.000.000/0000-00'
											/>
										</LabelContainer>
									)}
									{type === "consumer" && (
										<LabelContainer>
											<InputMask
												label='CPF'
												name='cpf'
												mask='999.999.999-99'
												placeholder='000.000.000-00'
											/>
										</LabelContainer>
									)}

									<LabelContainer>
										<Input
											label='Nova senha'
											name='password'
											type='password'
											placeholder='Nova senha'
										/>
									</LabelContainer>

									<LabelContainer>
										<Input
											label='Confirme nova senha'
											name='passwordConfirm'
											type='password'
											placeholder='Confirme nova senha'
										/>
									</LabelContainer>

									<button
										className='btn-primary'
										type='submit'
										disabled={submit ? "disabled" : ""}>
										{submit ? "enviando" : "enviar"}
									</button>
								</Form>
							</div>
						</div>
					</section>
				</>
			) : (
				<Success>
					<h1 className='title'>
						Sua senha foi redefinida com{" "}
						<span className='text-secundary'>sucesso.</span>
					</h1>
					<Link to={`${process.env.PUBLIC_URL}/login/${type}`} className='btn-primary'>
						fazer login
					</Link>
				</Success>
			)}
		</Container>
	);
};

export default ResetPassword;
